import { TickingDateAgo } from '@components/core-ui/Date/TickingDateAgo';
import { SwitchButton } from '@components/core-ui/form-elements/buttons/Button/variations/SwitchButton';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { Header } from '@components/Header';
import { HeaderSwitchWrapper } from '@components/Header/components/HeaderSwitchWrapper';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import CrossIcon from '@components/icons/Cross.svg';
import { EntityIcon } from '@components/core-ui/EntityIcon/';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NotificationsService } from 'app/API';
import { useInvalidateQueriesOnChange } from 'app/hooks/useInvalidateQueriesOnChange';
import { createFetchNotificationsQueryFn } from 'app/queries/notifications/createFetchNotificationsQueryFn';
import { notificationsKeys } from 'app/queries/notifications/notificationsKeys';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { MainContent } from './styles/MainContent';

export const NotificationsPage = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { notificationsReceiveDate, updateLastNotifications } = useAuthContext();
  const navigate = useNavigate();

  const notificationsQuery = useQuery({
    queryKey: notificationsKeys.list(),
    queryFn: createFetchNotificationsQueryFn(call),
  });
  useInvalidateQueriesOnChange(notificationsReceiveDate, notificationsKeys.list());

  const refreshNotifications = () => {
    void updateLastNotifications();
    return queryClient.invalidateQueries({ queryKey: notificationsKeys.list() });
  };

  const deleteMutation = useMutation({
    mutationFn: (notificationId: string) => call(() => NotificationsService.deleteNotification({ notificationId })),
    onError: () => toast.error('Failed to delete the notification'),
    onSettled: refreshNotifications,
  });

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => call(() => NotificationsService.setNotificationRead({ notificationId })),
    onError: () => toast.error('Failed to mark the notification as read'),
    onSettled: refreshNotifications,
  });

  const notifications = notificationsQuery.data;
  const isBusy = notificationsQuery.isFetching || deleteMutation.isPending || markReadMutation.isPending;

  let content: React.ReactElement | null = null;
  if (notifications?.length === 0) {
    content = (
      <div className="no-notifications">
        {"You'll"} get notified here when someone mentions you, your job status updates, and more
      </div>
    );
  }
  if (notifications?.length) {
    content = (
      <div className="notifications-list">
        {notifications.map((n) => {
          return (
            <div className="notification" key={n.id}>
              <EntityIcon
                name={n.Organization?.name || 'Auto Script'}
                src={n.Organization?.image ?? undefined}
                className="entity-name"
              />
              <div className="content">
                {!n.seen ? <div className="unseen-dot"></div> : null}
                <OutlinedButton
                  className="delete-button"
                  aria-label="Delete notification"
                  disabled={deleteMutation.isPending && deleteMutation.variables === n.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteMutation.mutate(n.id);
                  }}
                >
                  <CrossIcon />
                </OutlinedButton>
                <p className="org-name">{n.Organization?.name || 'AutoScript'}</p>
                <p className="message">{n.message}</p>
                {n.url ? (
                  <RouterLink to={n.url} className="link" onClick={() => markReadMutation.mutate(n.id)}>
                    {n.url.split('/').slice(3).join('/')}
                  </RouterLink>
                ) : null}
                <p className="created">
                  <span className="comment-date">
                    <TickingDateAgo date={n.createdAt} />
                  </span>
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Notifications - AutoScript</title>
      </Helmet>
      <Header
        title="Notifications"
        leftSideContent={[
          <HeaderSwitchWrapper key="tabSwitcher">
            <SwitchButton
              active
              onClick={() => {
                navigate(routes.notifications.make());
              }}
            >
              Notifications
            </SwitchButton>
            <SwitchButton
              onClick={() => {
                navigate(routes.notificationSettings.make());
              }}
            >
              Settings
            </SwitchButton>
          </HeaderSwitchWrapper>,
        ]}
      />

      <LinearProgressLoader active={isBusy} />
      <MainContent>
        {notificationsQuery.isError ? (
          <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
            <span>Failed to load notifications.</span>
            <TextButton type="button" onClick={() => void notificationsQuery.refetch()}>
              Retry
            </TextButton>
          </div>
        ) : null}
        {content}
      </MainContent>
    </>
  );
};
